import { Flex, useBreakpointValue } from '@chakra-ui/react';
import { useCallback } from 'react';
import { FilterIcon } from 'ui-components/dist/FilterIcon';
import { AddTalentButton } from '~/components/ManagerTopBar/components/AddTalentButton';
import { FEATURE_FLAGS } from '~/constants';
import { FEATURE_STATUS, useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { ClearAllFiltersButton } from '~/screens/MyAgency/components/ClearAllFiltersButton';
import { SearchTalent } from '~/screens/MyAgency/components/SearchTalent';
import { SortCreatorsDropDown } from '~/screens/MyAgency/components/SortCreatorsDropdown';
import { AgencyHeaderButton } from '~/screens/MyAgency/components/Table/AgencyHeaderButton';
import { ToggleView } from '~/screens/MyAgency/components/ToggleView';
import {
  useAdvancedFiltersStore,
  useCreatorsFiltersStore,
  useSearchTalentStore,
} from '~/screens/MyAgency/store';
import { allowedSortDirections, allowedSortFields } from '~/searchApi';
import { ICreator } from '~/types';

import { TalentPageFilterToggle } from '../TalentPageFiltersDrawer/TalentPageFilterToggle';
import { Header } from './Header';
import { HeaderViewBase } from './HeaderViewBase';
import { HeaderViewMd } from './HeaderViewMd';
import { ShareRosterListBtn } from './ShareRosterListBtn';

interface HeaderViewProps {
  agencyData: ICreator[];
  logo: string;
  displaySaveListButton: boolean;
  isShared: boolean;
  isAgencyEnabled: boolean;
  enabledControls: string[];
  setSortingOrder: (
    field: allowedSortFields,
    direction: allowedSortDirections,
  ) => void;
  sortDirection: allowedSortDirections;
  sortField: allowedSortFields;
  title: string;
  totalCreators: number;
  hasFilters?: boolean;
}

export const HeaderView: React.FC<HeaderViewProps> = ({
  agencyData,
  logo,
  displaySaveListButton,
  isShared,
  setSortingOrder,
  sortDirection,
  sortField,
  title,
  isAgencyEnabled,
  enabledControls,
  totalCreators,
  hasFilters = false,
}) => {
  const savedListSaveFeature = useFeatureFlag(FEATURE_FLAGS.foamSavedListSave);
  const foamAdvancedFilters = useFeatureFlag(FEATURE_FLAGS.foamAdvancedFilters);
  const foamAddTalent = useFeatureFlag(FEATURE_FLAGS.foamAddTalent);

  const isSavedListSaveEnabled =
    savedListSaveFeature === FEATURE_STATUS.enabled;

  const { query } = useSearchTalentStore();

  const isBaseBreakpoint = useBreakpointValue({ base: true, md: false });
  const isMdBreakpoint = useBreakpointValue({
    base: false,
    md: true,
    lg: false,
  });
  const {
    setIsDrawerOpen: openFiltersDrawer,
    appliedFilters,
    clearFilters: clearAdvancedFilters,
  } = useAdvancedFiltersStore();
  const {
    clearAllFilters: clearCreatorsFilters,
    openCreatorsFiltersDrawer,
    appliedFilters: appliedCreatorFilters,
  } = useCreatorsFiltersStore();

  const isTalentFiltersEnabled =
    useFeatureFlag(FEATURE_FLAGS.foamTalentFilters) === 'enabled' || isShared;

  const isAudienceFiltersEnabled =
    isShared && enabledControls
      ? enabledControls.includes('audience-filters')
      : foamAdvancedFilters === 'enabled';

  const isTalentSearchEnabled =
    useFeatureFlag(FEATURE_FLAGS.foamRosterTalentSearch) === 'enabled';

  const globalTalentFiltersFeature = useFeature(
    FEATURE_FLAGS.foamTalentGlobalFilters,
  );

  const handleClearAllFilters = () => {
    // do not memoize this function handleClearAllFilters
    // Memoizing this could create issues with the query params and cause old values to be used
    clearAdvancedFilters();
    setTimeout(() => {
      clearCreatorsFilters();
    }, 100);
  };

  const handleTalentClick = useCallback(() => {
    openCreatorsFiltersDrawer();
  }, [openCreatorsFiltersDrawer]);

  const handleAudienceClick = useCallback(() => {
    openFiltersDrawer(true);
  }, [openFiltersDrawer]);

  const isFiltersDisabled = agencyData.length === 0 && !hasFilters && !query;

  if (isBaseBreakpoint) {
    return (
      <HeaderViewBase
        agencyData={agencyData}
        sortField={sortField}
        sortDirection={sortDirection}
        setSortingOrder={setSortingOrder}
        logo={logo}
        title={title}
        isAgencyEnabled={isAgencyEnabled}
        isShared={isShared}
        displaySaveListButton={displaySaveListButton && isSavedListSaveEnabled}
        onAudienceClick={handleAudienceClick}
        onClearAllFilters={handleClearAllFilters}
        onTalentClick={handleTalentClick}
        isAudienceFiltersEnabled={isAudienceFiltersEnabled}
        isTalentFiltersEnabled={isTalentFiltersEnabled}
        isTalentSearchEnabled={isTalentSearchEnabled}
        isFiltersDisabled={isFiltersDisabled}
        totalCreators={totalCreators}
      />
    );
  }

  if (isMdBreakpoint) {
    return (
      <HeaderViewMd
        agencyData={agencyData}
        sortField={sortField}
        sortDirection={sortDirection}
        setSortingOrder={setSortingOrder}
        logo={logo}
        title={title}
        isShared={isShared}
        isAgencyEnabled={isAgencyEnabled}
        displaySaveListButton={displaySaveListButton && isSavedListSaveEnabled}
        onAudienceClick={handleAudienceClick}
        onClearAllFilters={handleClearAllFilters}
        onTalentClick={handleTalentClick}
        isAudienceFiltersEnabled={isAudienceFiltersEnabled}
        isTalentFiltersEnabled={isTalentFiltersEnabled}
        isTalentSearchEnabled={isTalentSearchEnabled}
        totalCreators={totalCreators}
      />
    );
  }

  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      gap={2}
      marginBottom="spacing-300"
      flexWrap="wrap"
      paddingTop="spacing-200"
    >
      <Header logo={logo} title={title} isAgencyEnabled={isAgencyEnabled} />
      <Flex justifyContent="flex-end" alignItems="center" gap={2}>
        <Flex
          justifyContent="flex-end"
          alignItems="center"
          gap={2}
          pointerEvents={isFiltersDisabled ? 'none' : 'auto'}
        >
          {(appliedFilters.length > 0 || appliedCreatorFilters.length > 0) &&
            isAudienceFiltersEnabled && (
              <ClearAllFiltersButton onClear={handleClearAllFilters} />
            )}
          {isTalentSearchEnabled && <SearchTalent />}
          <SortCreatorsDropDown
            sortField={sortField}
            sortDirection={sortDirection}
            setSortingOrder={setSortingOrder}
          />

          {globalTalentFiltersFeature.isEnabled && <TalentPageFilterToggle />}

          {isTalentFiltersEnabled && !globalTalentFiltersFeature.isEnabled && (
            <AgencyHeaderButton
              onClick={handleTalentClick}
              icon={FilterIcon}
              title="Talent"
              aria-label="Talent Filter"
              iconOnly={false}
              track-id="foam-myagency-talent-filters"
            />
          )}
          {isAudienceFiltersEnabled &&
            !globalTalentFiltersFeature.isEnabled && (
              <AgencyHeaderButton
                onClick={handleAudienceClick}
                icon={FilterIcon}
                title="Audience"
                aria-label="Audience Filters"
                iconOnly={false}
                iconProps={{ boxSize: 4 }}
              />
            )}
          <ToggleView />
        </Flex>
        {!displaySaveListButton && foamAddTalent === 'enabled' && (
          <AddTalentButton />
        )}

        {!isShared && <ShareRosterListBtn />}
      </Flex>
    </Flex>
  );
};
